import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { signUp } from "../lib/auth";
import { api } from "../lib/api";
import { useInviteValidation } from "../hooks/useInviteValidation";
import { Button, Input, Label, Checkbox } from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { AuthLayout } from "../components/auth/AuthLayout";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      invite: (search.invite as string) || undefined,
    };
  },
});

const SIGNUP_ENABLED = import.meta.env.VITE_SIGNUP_ENABLED === "true";
const MARKETING_URL = "https://wondermaps.co";

function SignupPage() {
  const { invite: inviteFromUrl } = useSearch({ from: "/signup" });
  const { isValid: inviteIsValid, isLoading: isValidatingInvite } =
    useInviteValidation(inviteFromUrl ?? "");

  const inviteBypass = !!inviteFromUrl && inviteIsValid;
  const stillCheckingInvite = !!inviteFromUrl && isValidatingInvite;

  if (!SIGNUP_ENABLED && !inviteBypass && !stillCheckingInvite) {
    return (
      <AuthLayout
        title="Wondermaps is invite-only"
        subtitle="We're not accepting new public sign-ups right now."
      >
        <div className="space-y-4 text-center">
          <p className="text-sm text-muted-foreground">
            If you're interested in Wondermaps for your organization, please get in touch via our marketing site.
          </p>
          <Button asChild className="w-full">
            <a href={MARKETING_URL}>Visit wondermaps.co</a>
          </Button>
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" search={{ redirect: "/" }} className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  return <SignupForm inviteFromUrl={inviteFromUrl} />;
}

function SignupForm({ inviteFromUrl }: { inviteFromUrl: string | undefined }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInviteCode, setHasInviteCode] = useState(!!inviteFromUrl);
  const [inviteCode, setInviteCode] = useState(inviteFromUrl ?? "");

  const { isValid, orgName, role, isLoading: isValidating } = useInviteValidation(
    hasInviteCode ? inviteCode : ""
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await signUp.email({
        email,
        password,
        name,
      });

      if (result.error) {
        setError(result.error.message || "Signup failed");
        return;
      }

      // If valid invite code, redeem it after signup
      if (hasInviteCode && isValid && inviteCode) {
        const redeemRes = await api.invites[":code"].redeem.$post({
          param: { code: inviteCode },
        });
        if (redeemRes.ok) {
          const data = await redeemRes.json();
          window.location.href = `/${data.organizationSlug}/admin`;
          return;
        }
        // Account created but redeem failed - still redirect, user can join later
        console.error("Failed to redeem invite code after signup");
      }

      window.location.href = "/";
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title="Create Account" subtitle="Sign up to get started with Wondermaps">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8–128 characters"
              required
              minLength={8}
              maxLength={128}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <FontAwesomeIcon icon={faEyeSlash} className="h-[18px] w-[18px]" /> : <FontAwesomeIcon icon={faEye} className="h-[18px] w-[18px]" />}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="hasInvite"
              checked={hasInviteCode}
              onCheckedChange={(checked) => setHasInviteCode(!!checked)}
            />
            <Label htmlFor="hasInvite" className="text-sm font-normal cursor-pointer">
              I have an organization invite code
            </Label>
          </div>

          {hasInviteCode && (
            <div className="space-y-2">
              <Input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter invite code"
              />
              {inviteCode && (
                <div className="text-sm">
                  {isValidating ? (
                    <span className="text-muted-foreground">Validating...</span>
                  ) : isValid && orgName ? (
                    <span className="text-green-600">✓ {orgName} ({role})</span>
                  ) : inviteCode.length > 0 ? (
                    <span className="text-destructive">Invalid invite code</span>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Creating account..." : "Sign Up"}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground pt-4">
        Already have an account?{" "}
        <Link
          to="/login"
          search={{
            redirect: inviteFromUrl
              ? `/join?code=${inviteFromUrl}`
              : "/",
          }}
          className="text-primary hover:underline"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
